import { useEffect, useRef, useCallback } from 'react';

interface UseIntersectionObserverProps {
  onIntersect: () => void;
  threshold?: number;
  rootMargin?: string;
}

export function useIntersectionObserver({
  onIntersect,
  threshold = 0.1,
  rootMargin = '0px',
}: UseIntersectionObserverProps) {
  const elementRef = useRef<HTMLElement>(null);
  const onIntersectRef = useRef(onIntersect);
  onIntersectRef.current = onIntersect;
  const thresholdRef = useRef(threshold);
  const rootMarginRef = useRef(rootMargin);

  const stableCallback = useCallback(() => {
    onIntersectRef.current();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          stableCallback();
          observer.unobserve(entry.target);
        }
      },
      { threshold: thresholdRef.current, rootMargin: rootMarginRef.current }
    );

    const currentElement = elementRef.current;

    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
      observer.disconnect();
    };
  }, [stableCallback]);

  return elementRef;
}